import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import axios from '../utils/axiosInstance';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { localeLabel } from '../utils/localeFlag';
import useTargetLocales from '../hooks/useTargetLocales';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '../components/ui/table';

// Per-row status badge — LIFTED VERBATIM from ContentResources.jsx:81-91
// per D-12 + UI-SPEC inline-duplication invariant. The same map is also
// inlined in ProductResources.jsx (Wave 3); intentional duplication, NOT
// extracted to a shared module. Phase 20 vocab (`pushed | pending | failed`)
// matches this map; legacy `translated/partial/outdated` are not supported
// here on purpose. Pitfall 4: do NOT import the legacy
// `'../components/StatusBadge'` — vocabulary mismatch.
function StatusBadge({ status }) {
  if (!status) return <span className="text-muted-foreground">—</span>;
  const map = {
    pending: { className: 'bg-gray-100 text-gray-800 hover:bg-gray-100', label: 'Pending' },
    translated: { className: 'bg-blue-100 text-blue-800 hover:bg-blue-100', label: 'Translated' },
    pushed: { className: 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100', label: 'Pushed' },
    failed: { className: 'bg-red-100 text-red-800 hover:bg-red-100', label: 'Failed' },
  };
  const cfg = map[status] || { className: '', label: status };
  return <Badge className={cfg.className}>{cfg.label}</Badge>;
}

// truncate — LIFTED VERBATIM from ContentResources.jsx:129-132 per D-12.
function truncate(value, n = 80) {
  if (!value) return '';
  return value.length > n ? value.slice(0, n) + '…' : value;
}

// Resolve the effective policy for a row. Precedence is field default → row
// override → auto_skip_reason → 'auto'. Explicit operator choice (from
// Settings → Field Defaults or a row override) wins over auto-classification
// so the badge reflects what'll actually run, not the auto layer underneath.
function effectivePolicyValue(row) {
  if (row.effective_policy) return row.effective_policy;
  if (row.auto_skip_reason) return 'auto';
  return 'auto';
}

// Read-only badge for the policy column. Editing happens in Settings → Field
// Defaults; this page is now display-only so users have one place to change
// behavior. Color matches /metafields/<id> policy badges for consistency.
function PolicyBadge({ row }) {
  const value = effectivePolicyValue(row);
  const cls = value === 'translate'
    ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-300'
    : 'bg-muted text-muted-foreground';
  return (
    <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded capitalize ${cls}`}>
      {value}
    </span>
  );
}

// Surface the field-level default when the per-row policy is unset, so the
// user understands the value came from Settings → Field Defaults rather than
// a per-row override. Returns null when there's nothing useful to add.
function FieldDefaultHint({ row }) {
  if (row.translation_policy !== null) return null;
  if (!row.field_default_policy) return null;
  return (
    <span className="block text-[10px] text-muted-foreground mt-1">
      from default
    </span>
  );
}

/**
 * Phase 21 Wave 4 — drill-in for one product showing PRODUCT +
 * PRODUCT_OPTION + PRODUCT_OPTION_VALUE rows with per-row policy dropdown +
 * per-locale status badges.
 *
 * Route: `/products/:productId/resources` (registered by Wave 2 App.jsx).
 * `useParams()` extracts the bare numeric productId; the component
 * reconstructs the full Shopify GID via `gid://shopify/Product/${productId}`.
 *
 * Two parallel fetches on mount per D-10:
 *   1. by-resource-id endpoint for the parent PRODUCT row (per-field rows
 *      ordered by field_key).
 *   2. Wave 1 D-14 `?parent_product_id=<gid>&page_size=5000` filter for ALL
 *      child PRODUCT_OPTION + PRODUCT_OPTION_VALUE rows (Pitfall 3
 *      page_size cap — global pagination at 5000 fits ~55 typical rows
 *      comfortably).
 *
 * Option-tree rendered open by default per D-10 — single-product page can
 * afford eager-load. No collapse-toggle state. (Differs from the LIST page
 * where the tree is collapsed by default with lazy-load on first open.)
 *
 * NO PIM JOIN per A8 — drill-in doesn't need vendor/product_type enrichment;
 * the title is denormalised into product_resources per Phase 20 D-03.
 *
 * NO push-to-Shopify path per D-11 spirit / Phase 22 deferral — drill-in is
 * read-only on the inventory + per-row policy mutation only.
 *
 * NO kill-switch flag check per Pitfall 9 + A5 — this component is only
 * navigable from the new `ProductResources.jsx` (which only mounts when the
 * flag is OFF). The legacy `ProductDetail.jsx` lives at the different
 * `/products/:id` route and stays untouched.
 *
 * Inline helpers (`StatusBadge`, `truncate`) lifted VERBATIM from
 * ContentResources.jsx:81-128 per D-12 + UI-SPEC inline-duplication invariant.
 * The policy column on this page is read-only (display via `PolicyBadge`);
 * the editable surface lives in Settings → Field Defaults, so the legacy
 * `dropdownValue` and per-row PATCH callback have been removed here.
 */
export default function ProductResourceDetail() {
  const { productId } = useParams();
  const navigate = useNavigate();
  // Phase 20 contract: rows carry FULL Shopify GIDs in `parent_product_id`
  // and `resource_id`. Reconstruct from the bare numeric route param so the
  // by-resource-id and parent_product_id queries get the correct prefix
  // (T-21-W4-01 — backend GID prefix validation rejects malformed values).
  const productGid = `gid://shopify/Product/${productId}`;

  const [parentRows, setParentRows] = useState([]);
  const [childRows, setChildRows] = useState([]);
  // Metafields come from the legacy /product-content/ endpoint (which already
  // serves nested resources for ProductDetail.jsx). Phase 21's product_resources
  // schema CHECKs ('PRODUCT', 'PRODUCT_OPTION', 'PRODUCT_OPTION_VALUE') so it
  // doesn't carry metafields — those live in transly.metafield_definitions /
  // _values. One parallel fetch per allowed locale so each metafield row shows
  // all translations side-by-side.
  const [metafieldsByLocale, setMetafieldsByLocale] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { locales } = useTargetLocales();

  // D-10 — two parallel fetches on mount. Promise.all awaits both before
  // committing state so the table renders in a single React commit (avoids
  // the "parent rows render then children appear half a second later"
  // flicker). The by-resource-id URL needs encodeURIComponent because the
  // GID path contains forward slashes that would otherwise be parsed as URL
  // segment separators.
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Metafields via the legacy /product-content/ endpoint. The endpoint
      // expects the BARE numeric productId (it builds the GID server-side
      // at views.py:253) — passing the full GID double-prefixes it and
      // 404s silently. One fetch per allowed locale so the metafields
      // section can render all translations side-by-side. Each catch
      // independently — a single locale failing does NOT block the others
      // or the core option-tree.
      const localeFetches = locales.map((loc) =>
        axios
          .get('/api/transly/product-content/', {
            params: { productId, locale: loc },
          })
          .catch(() => ({ data: { nestedResources: [] } })),
      );
      const responses = await Promise.all([
        axios.get(
          `/api/transly/product-resources/by-resource-id/${encodeURIComponent(productGid)}/`,
        ),
        // Pitfall 3 — Wave 1 D-14 filter. page_size: 5000 is the
        // _MAX_PAGE_SIZE cap; without Wave 1's `parent_product_id` filter
        // this would return ALL product_resources rows (filter ignored) and
        // crash the UI. Realistic max for one product is ~55 rows
        // (5 options × 10 values + parent fields), so the cap is comfortable
        // headroom and not a DoS surface (T-21-W4-05).
        axios.get('/api/transly/product-resources/', {
          params: { parent_product_id: productGid, page_size: 5000 },
        }),
        ...localeFetches,
      ]);
      const parentRes = responses[0];
      const childrenRes = responses[1];
      const localeResponses = responses.slice(2);
      setParentRows(parentRes.data?.rows || []);
      setChildRows(childrenRes.data?.rows || []);
      const filterMf = (res) =>
        (res.data?.nestedResources || []).filter((n) =>
          n.resourceId?.includes('/Metafield/'),
        );
      const byLocale = {};
      locales.forEach((loc, i) => {
        byLocale[loc] = filterMf(localeResponses[i]);
      });
      setMetafieldsByLocale(byLocale);
    } catch (err) {
      setError(
        err.response?.data?.error
          || err.message
          || 'Failed to load product resources',
      );
    } finally {
      setLoading(false);
    }
  }, [productGid, productId, locales]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2 mb-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/products')}
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Products
            </Button>
          </div>
          <CardTitle>
            {parentRows[0]?.title || `Product ${productId}`}
          </CardTitle>
          {parentRows.length > 0 && (
            <a
              href={`https://admin.shopify.com/store/lamptwist-live/products/${productId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mt-1"
            >
              View in Shopify Admin <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </CardHeader>

        <CardContent>
          {error && (
            <div className="rounded-md border border-red-200 bg-red-50 text-red-800 p-3 mb-4 text-sm">
              {error}
            </div>
          )}

          {loading && (
            <div className="p-4 text-sm text-muted-foreground">Loading...</div>
          )}

          {/* D-09 + Phase 18 D-14 mirror — empty-state CTA when this product
              has no rows in the local inventory yet. The backend returns
              `{rows: []}` for unknown gids (T-21-W4-06 mitigation), so this
              branch also covers direct-URL navigation to a non-existent
              product. Operators sync from the Products inventory page (the
              drill-in is read-only on the inventory). */}
          {!loading && parentRows.length === 0 && (
            <div className="p-8 text-center">
              <p className="text-muted-foreground mb-4">
                No translations stored locally for this product. Run a sync
                from the Products inventory page to populate.
              </p>
              <Link to="/products" className="inline-block">
                <Button variant="outline">
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  Back to Products
                </Button>
              </Link>
            </div>
          )}

          {/* D-10 — option-tree rendered open by default. Rows render
              inline grouped by resource_type: PRODUCT field rows (from
              parentRows) first, then PRODUCT_OPTION rows, then
              PRODUCT_OPTION_VALUE rows (both filtered from childRows).
              No collapse-toggle state — single-product page can afford
              eager-load (~55 rows max per Pitfall 3). Per-row template
              lifted from ContentResources.jsx:464-491 shape (field_key,
              raw_value truncated, policy badge (read-only), DE StatusBadge, ES
              StatusBadge). No Shopify Admin links per row — the header
              has one Admin link to the parent product, and option/value
              rows don't exist as standalone admin URLs. No push pipeline
              wired in — that surface is Phase 22 territory. */}
          {!loading && parentRows.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Field</TableHead>
                  <TableHead>Value</TableHead>
                  <TableHead className="w-32">Policy</TableHead>
                  {locales.map((loc) => (
                    <TableHead key={loc} className="w-24">{localeLabel(loc)}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {/* PRODUCT field rows — title, body_html, handle,
                    product_type, meta_title, meta_description (whichever
                    field_keys the Phase 20 sync surfaced). */}
                {parentRows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="font-medium text-sm font-mono">
                      {row.field_key}
                    </TableCell>
                    <TableCell className="max-w-md">
                      <span title={row.raw_value || ''}>
                        {truncate(row.raw_value, 100)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <PolicyBadge row={row} />
                      <FieldDefaultHint row={row} />
                    </TableCell>
                    {locales.map((loc) => (
                      <TableCell key={loc}><StatusBadge status={row[`${loc}_status`]} /></TableCell>
                    ))}
                  </TableRow>
                ))}

                {/* Section divider for PRODUCT_OPTION rows — only rendered
                    when at least one option row exists. Visual separation
                    between the parent product fields and its options. */}
                {childRows.some((c) => c.resource_type === 'PRODUCT_OPTION') && (
                  <TableRow>
                    <TableCell
                      colSpan={3 + locales.length}
                      className="bg-muted/40 text-xs font-medium uppercase text-muted-foreground tracking-wide py-2"
                    >
                      Product options
                    </TableCell>
                  </TableRow>
                )}

                {/* PRODUCT_OPTION rows — display the option name (e.g.
                    "Color", "Size") with a `↳ option` indent indicator.
                    Same per-row template as PRODUCT rows. */}
                {childRows
                  .filter((c) => c.resource_type === 'PRODUCT_OPTION')
                  .map((row) => (
                    <TableRow key={row.id} className="bg-muted/10">
                      <TableCell className="text-sm pl-6 font-mono">
                        <span className="text-muted-foreground mr-2">↳ option</span>
                        {row.field_key}
                      </TableCell>
                      <TableCell className="max-w-md">
                        <span title={row.raw_value || ''}>
                          {truncate(row.title || row.raw_value, 100)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <PolicyBadge row={row} />
                        <FieldDefaultHint row={row} />
                      </TableCell>
                      {locales.map((loc) => (
                        <TableCell key={loc}><StatusBadge status={row[`${loc}_status`]} /></TableCell>
                      ))}
                    </TableRow>
                  ))}

                {/* Section divider for PRODUCT_OPTION_VALUE rows — only
                    rendered when at least one option-value row exists. */}
                {childRows.some((c) => c.resource_type === 'PRODUCT_OPTION_VALUE') && (
                  <TableRow>
                    <TableCell
                      colSpan={3 + locales.length}
                      className="bg-muted/40 text-xs font-medium uppercase text-muted-foreground tracking-wide py-2"
                    >
                      Option values
                    </TableCell>
                  </TableRow>
                )}

                {/* PRODUCT_OPTION_VALUE rows — display the option-value
                    text (e.g. "Red", "Large") with a `↳ value` indent. */}
                {childRows
                  .filter((c) => c.resource_type === 'PRODUCT_OPTION_VALUE')
                  .map((row) => (
                    <TableRow key={row.id} className="bg-muted/10">
                      <TableCell className="text-sm pl-6 font-mono">
                        <span className="text-muted-foreground mr-2">↳ value</span>
                        {row.field_key}
                      </TableCell>
                      <TableCell className="max-w-md">
                        <span title={row.raw_value || ''}>
                          {truncate(row.title || row.raw_value, 100)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <PolicyBadge row={row} />
                        <FieldDefaultHint row={row} />
                      </TableCell>
                      {locales.map((loc) => (
                        <TableCell key={loc}><StatusBadge status={row[`${loc}_status`]} /></TableCell>
                      ))}
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Metafields — sourced from /product-content/ (legacy nestedResources)
          rather than product_resources, since the Phase 20 schema doesn't
          carry metafields. One fetch per allowed locale; merged by resourceId
          so each row shows source (EN) + one translation column per locale.
          Renders only when at least one locale has data; missing-translation
          cells show muted "not translated" placeholder. */}
      {(() => {
        // Merge per-locale metafield arrays by resourceId. Any locale can be
        // empty (single-locale fetch failed); we union the keys so rows are
        // present even when one locale has no data for that metafield.
        const byId = new Map();
        for (const loc of locales) {
          for (const mf of (metafieldsByLocale[loc] || [])) {
            const prev = byId.get(mf.resourceId) || {};
            byId.set(mf.resourceId, { ...prev, [loc]: mf });
          }
        }
        if (byId.size === 0) return null;
        const merged = [...byId.values()];
        return (
          <Card className="mt-4">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300">
                  Metafields
                </span>
                <span className="text-sm text-muted-foreground font-normal">
                  {merged.length} item{merged.length !== 1 ? 's' : ''}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Metafield</TableHead>
                    <TableHead>Field</TableHead>
                    <TableHead>Source (EN)</TableHead>
                    {locales.map((loc) => (
                      <TableHead key={loc}>{localeLabel(loc)}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {merged.flatMap((mfByLocale) => {
                    const ref = locales.map((loc) => mfByLocale[loc]).find(Boolean);
                    if (!ref) return [];
                    const label = ref.displayName
                      || ref.translatableContent?.[0]?.value
                      || ref.resourceId.split('/').pop();
                    // Use the (richer) source-content list as the field set
                    // — translations are looked up by key from each locale.
                    const fields = (ref.translatableContent || []).filter((f) => f.value);
                    if (fields.length === 0) return [];
                    return fields.map((f, idx) => (
                      <TableRow key={`${ref.resourceId}:${f.key}`}>
                        {idx === 0 ? (
                          <TableCell className="font-medium align-top" rowSpan={fields.length}>
                            {label}
                          </TableCell>
                        ) : null}
                        <TableCell className="text-xs text-muted-foreground align-top">
                          {f.key}
                        </TableCell>
                        <TableCell className="text-sm align-top">
                          {truncate(f.value, 120)}
                        </TableCell>
                        {locales.map((loc) => {
                          const tr = mfByLocale[loc]?.translations?.find((t) => t.key === f.key);
                          return (
                            <TableCell key={loc} className="text-sm text-primary align-top">
                              {tr?.value
                                ? truncate(tr.value, 120)
                                : <span className="text-muted-foreground italic">not translated</span>}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ));
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        );
      })()}
    </div>
  );
}
